import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Package, LogOut, ArrowRight, Loader2, Inbox } from "lucide-react";
import { api } from "@/lib/accessApi";

export default function ProductDashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, email: "", products: [], checkoutUrl: "" });

  useEffect(() => {
    (async () => {
      try {
        const me = await api("/me");
        const { products } = await api("/my/products");
        let checkoutUrl = "";
        try { checkoutUrl = (await api("/access/config")).checkout_url || ""; } catch { /* ignore */ }
        setState({ loading: false, email: me.email, products, checkoutUrl });
      } catch {
        navigate("/access", { replace: true });
      }
    })();
  }, [navigate]);

  const logout = async () => {
    try { await api("/access/logout", { method: "POST" }); } catch { /* ignore */ }
    navigate("/access", { replace: true });
  };

  if (state.loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="border-b border-white/10 bg-slate-900/50 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <div>
            <p className="text-xs text-slate-400">Signed in as</p>
            <p className="text-sm font-semibold text-white" data-testid="dashboard-email">{state.email}</p>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-xl font-bold text-white">Your Products</h1>
        <p className="mt-1 text-sm text-slate-400">Digital products unlocked with your access code.</p>

        {state.products.length === 0 ? (
          <div className="mt-8 flex flex-col items-center rounded-2xl border border-dashed border-white/15 bg-white/5 p-12 text-center" data-testid="dashboard-empty">
            <Inbox className="h-8 w-8 text-slate-500" />
            <p className="mt-3 text-sm text-slate-400">Belum ada produk berbayar di akun ini.</p>
            <p className="mt-1 text-xs text-slate-500">Akses diberikan otomatis setelah pembayaran di Lynk.id berhasil.</p>
            {state.checkoutUrl && (
              <a href={state.checkoutUrl} target="_blank" rel="noopener noreferrer" data-testid="checkout-btn"
                className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-indigo-500 to-sky-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">
                Beli di Lynk.id <ArrowRight className="h-4 w-4" />
              </a>
            )}
          </div>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {state.products.map((p) => (
              <Link
                key={p.id} to={`/product/${p.id}`} data-testid={`product-card-${p.id}`}
                className="group rounded-2xl border border-white/10 bg-white/5 p-5 transition-colors hover:border-indigo-400/40 hover:bg-white/10"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-sky-500">
                  <Package className="h-5 w-5 text-white" />
                </span>
                <h3 className="mt-3 font-semibold text-white">{p.name}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-slate-400">{p.description}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-indigo-300 group-hover:gap-2">
                  Open <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
